//app/frontend/components/PrivateLessons/TimeSlotDrawer.tsx
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Title,
  Button,
  Group,
  Stack,
  TextInput,
  Select,
  MultiSelect,
  Divider,
  NumberInput,
  Switch,
  Text,
  LoadingOverlay,
} from '@mantine/core';
import { DatePickerInput, TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconCalendarTime, IconPlus, IconPencil } from '@tabler/icons-react';
import dayjs from 'dayjs';

interface TimeSlot {
  id: string;
  start_time: string;
  end_time: string;
  available: boolean;
  status: 'available' | 'pending' | 'booked' | 'cancelled' | 'completed' | 'no-show';
  booking?: {
    id: string;
    status: string;
  };
}

interface TimeSlotDrawerProps {
  opened: boolean;
  onClose: () => void;
  type: 'create' | 'edit';
  timeSlot: TimeSlot | null;
  onCreateTimeSlots: (data: any) => void;
  onUpdateTimeSlot: (id: string, data: Partial<TimeSlot>) => void;
  isSubmitting: boolean;
}

const DAYS_OF_WEEK = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

const DURATION_OPTIONS = [
  { value: '30', label: '30 minutes' },
  { value: '45', label: '45 minutes' },
  { value: '60', label: '1 hour' },
  { value: '90', label: '1.5 hours' },
  { value: '120', label: '2 hours' },
  { value: 'custom', label: 'Custom duration' },
];

export const TimeSlotDrawer: React.FC<TimeSlotDrawerProps> = ({
  opened,
  onClose,
  type,
  timeSlot,
  onCreateTimeSlots,
  onUpdateTimeSlot,
  isSubmitting,
}) => {
  const [useCustomDuration, setUseCustomDuration] = useState(false);

  // Single Time Slot Edit Form
  const editForm = useForm({
    initialValues: {
      start_time: timeSlot ? new Date(timeSlot.start_time) : new Date(),
      end_time: timeSlot ? new Date(timeSlot.end_time) : new Date(),
      available: timeSlot ? timeSlot.available : true,
      status: timeSlot ? timeSlot.status : 'available',
    },
    validate: {
      start_time: (value) => (!value ? 'Start time is required' : null),
      end_time: (value, values) => {
        if (!value) return 'End time is required';
        if (new Date(value) <= new Date(values.start_time)) {
          return 'End time must be after start time';
        }
        return null;
      },
    },
  });

  // Batch Create Form
  const batchForm = useForm({
    initialValues: {
      date_range_start: new Date(),
      date_range_end: dayjs().add(30, 'day').toDate(),
      days_of_week: ['1', '2', '3', '4', '5'], // Mon-Fri by default
      start_time: dayjs().hour(9).minute(0).second(0).toDate(),
      end_time: dayjs().hour(17).minute(0).second(0).toDate(),
      duration: '60',
      custom_duration: 60,
      buffer_time: 15,
      max_slots: 20,
    },
    validate: {
      date_range_start: (value) => (!value ? 'Start date is required' : null),
      date_range_end: (value, values) => {
        if (!value) return 'End date is required';
        if (new Date(value) < new Date(values.date_range_start)) {
          return 'End date must be after or equal to start date';
        }
        return null;
      },
      days_of_week: (value) => (value.length === 0 ? 'Select at least one day of the week' : null),
      start_time: (value) => (!value ? 'Start time is required' : null),
      end_time: (value, values) => {
        if (!value) return 'End time is required';

        // Compare only the time part
        const startHour = dayjs(values.start_time).hour();
        const startMinute = dayjs(values.start_time).minute();
        const endHour = dayjs(value).hour();
        const endMinute = dayjs(value).minute();

        if (endHour < startHour || (endHour === startHour && endMinute <= startMinute)) {
          return 'End time must be after start time';
        }
        return null;
      },
      duration: (value) => (!value ? 'Duration is required' : null),
      custom_duration: (value, values) => {
        if (values.duration === 'custom' && (!value || value <= 0)) {
          return 'Custom duration must be greater than 0';
        }
        return null;
      },
    },
  });

  // Reset forms when drawer opens/closes or timeSlot changes
  useEffect(() => {
    if (opened) {
      if (type === 'edit' && timeSlot) {
        editForm.setValues({
          start_time: new Date(timeSlot.start_time),
          end_time: new Date(timeSlot.end_time),
          available: timeSlot.available,
          status: timeSlot.status,
        });
      } else {
        batchForm.reset();
      }
    }
  }, [opened, type, timeSlot]);

  // Handle Edit Form Submission
  const handleEditSubmit = (values: typeof editForm.values) => {
    if (!timeSlot) return;

    onUpdateTimeSlot(timeSlot.id, {
      start_time: values.start_time.toISOString(),
      end_time: values.end_time.toISOString(),
      available: values.available,
      status: values.status,
    });
  };

  // Handle Batch Create Form Submission
  const handleBatchSubmit = (values: typeof batchForm.values) => {
    // Extract values from form
    const {
      date_range_start,
      date_range_end,
      days_of_week,
      start_time,
      end_time,
      duration,
      custom_duration,
      buffer_time,
    } = values;

    // Convert days of week to numbers
    const selectedDays = days_of_week.map((day) => parseInt(day));

    // Calculate slot duration in minutes
    const slotDuration = duration === 'custom' ? custom_duration : parseInt(duration);

    // Generate time slots
    const generatedTimeSlots = [];
    let currentDate = dayjs(date_range_start);
    const endDate = dayjs(date_range_end);

    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      // Check if current day is in selected days
      if (selectedDays.includes(currentDate.day())) {
        // Set start and end times for this day
        let slotStart = dayjs(currentDate).hour(dayjs(start_time).hour()).minute(dayjs(start_time).minute()).second(0);

        const dayEnd = dayjs(currentDate).hour(dayjs(end_time).hour()).minute(dayjs(end_time).minute()).second(0);

        // Generate slots for this day
        while (
          slotStart.add(slotDuration, 'minute').isBefore(dayEnd) ||
          slotStart.add(slotDuration, 'minute').isSame(dayEnd)
        ) {
          const slotEnd = slotStart.add(slotDuration, 'minute');

          generatedTimeSlots.push({
            start_time: slotStart.toISOString(),
            end_time: slotEnd.toISOString(),
            available: true,
            status: 'available',
          });

          // Add buffer time
          slotStart = slotEnd.add(buffer_time, 'minute');
        }
      }

      // Move to next day
      currentDate = currentDate.add(1, 'day');
    }

    onCreateTimeSlots(generatedTimeSlots);
  };

  // Toggle custom duration
  const handleDurationChange = (value: string) => {
    batchForm.setFieldValue('duration', value);
    setUseCustomDuration(value === 'custom');
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={<Title order={3}>{type === 'create' ? 'Add New Time Slots' : 'Edit Time Slot'}</Title>}
      position="right"
      size="lg"
      padding="lg"
      styles={{
        content: { backgroundColor: '#3d3c3e' },
        header: { backgroundColor: '#2c2b2d', marginBottom: '2rem' },
        title: { color: '#f2f2f2', fontWeight: 600, fontSize: '1.5rem' },
      }}
    >
      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isSubmitting} />

        {type === 'edit' && timeSlot ? (
          <form onSubmit={editForm.onSubmit(handleEditSubmit)}>
            <Stack gap="md">
              <TimeInput label="Start Time" format="12" clearable required {...editForm.getInputProps('start_time')} />

              <TimeInput label="End Time" format="12" clearable required {...editForm.getInputProps('end_time')} />

              <Select
                label="Status"
                data={[
                  { value: 'available', label: 'Available' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'booked', label: 'Booked' },
                  { value: 'cancelled', label: 'Cancelled' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'no-show', label: 'No-Show' },
                ]}
                {...editForm.getInputProps('status')}
              />

              <Group gap="right" mt="xl">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" leftSection={<IconPencil size={16} />}>
                  Update Time Slot
                </Button>
              </Group>
            </Stack>
          </form>
        ) : (
          <form onSubmit={batchForm.onSubmit(handleBatchSubmit)}>
            <Stack gap="md">
              <Title order={5} className="poppins text-white">
                Date Range
              </Title>

              <Group grow>
                <DatePickerInput
                  label="Start Date"
                  placeholder="Select start date"
                  required
                  {...batchForm.getInputProps('date_range_start')}
                />

                <DatePickerInput
                  label="End Date"
                  placeholder="Select end date"
                  required
                  minDate={batchForm.values.date_range_start}
                  {...batchForm.getInputProps('date_range_end')}
                />
              </Group>

              <MultiSelect
                label="Days of Week"
                placeholder="Select days"
                data={DAYS_OF_WEEK}
                required
                {...batchForm.getInputProps('days_of_week')}
              />

              <Divider my="sm" label="Time Settings" labelPosition="center" />

              <Group grow>
                <TimeInput label="Daily Start Time" format="12" required {...batchForm.getInputProps('start_time')} />

                <TimeInput label="Daily End Time" format="12" required {...batchForm.getInputProps('end_time')} />
              </Group>

              <Select
                label="Lesson Duration"
                data={DURATION_OPTIONS}
                required
                value={batchForm.values.duration}
                onChange={handleDurationChange}
              />

              {useCustomDuration && (
                <NumberInput
                  label="Custom Duration (minutes)"
                  min={1}
                  step={5}
                  required
                  {...batchForm.getInputProps('custom_duration')}
                />
              )}

              <NumberInput
                label="Buffer Time Between Lessons (minutes)"
                description="Time between consecutive lessons"
                min={0}
                step={5}
                {...batchForm.getInputProps('buffer_time')}
              />

              <Divider my="sm" />

              <Text size="sm" color="dimmed">
                This will create time slots for the selected dates and times. All slots will be marked as "Available" by
                default.
              </Text>

              <Group gap="right" mt="xl">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" leftSection={<IconPlus size={16} />}>
                  Create Time Slots
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </div>
    </Drawer>
  );
};
